import { CourseApi } from "api";
import React, { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { coursesStatusApi } from "api/coursesStatusApi";
import {
  CourseSessionOption,
  TopicGroupOption,
  TopicOption,
  useCourseSessionsOptions,
  usePlacesOptions,
  useTopicGroupsOptions,
  useTopicsOptions,
} from "api/hooks";
import { ScheduleEventApi, useScheduleEvents } from "api/scheduleEvents";
import { TopicGroupApi } from "api/topicgroups/topicgroupsApi";
import { PaziaBox } from "components/atoms";
import { CourseRegistrationSettings, ToggleReadinessButton } from "components/molecules";
import { COURSE_PLACE_TYPE_OPTIONS } from "consts/courseScheduleType";
import { toMoneyFormat } from "utils";
import { QUERIES_KEYS } from "queries";
import { FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { components } from "react-select";
import { IDropdownOption, PaziaSelect } from "components/atoms";
import { fromTimeFormat } from "utils";
import { formatDate, minutesToTime, toTimeFormat } from "utils";
import { TabContent } from "../TabLayout";
import { EditCourseSchedulePreview } from "./EditCourseSchedulePreview";
import { TrainerCostsSection } from "./trainer-costs/TrainerCostsSection";

interface ScheduleStepProps {
  course: CourseApi;
}
export const ScheduleStep = ({ course }: ScheduleStepProps): JSX.Element | null => {
  const [experimental, setExperimental] = useState(false);
  const {
    data: { id: courseId },
  } = course;
  const queryClient = useQueryClient();

  const invalidate = async () => {
    queryClient.invalidateQueries({
      queryKey: QUERIES_KEYS.COURSE_BY_ID(Number(course?.data.id)),
    });
  };
  return (
    <TabContent>
      {experimental ? (
        <BulkScheduleEditTable courseId={courseId} courseGroupId={course.group.id} />
      ) : (
        <div className="space-y-4">
          <PaziaBox className="col-span-12 flex justify-between">
            <div className="flex grow flex-col gap-2 sm:flex-row">
              <div className="sm:w-1/2">
                <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Harmonogram</h2>
                <div className="mb-2 text-sm">
                  Szczegółowy harmonogram jest widoczny dla kursantów po zapisaniu się na kurs. <br />
                  <b>Niezatwierdzony</b> harmonogram będzie ukryty przed kursantami. <br />
                  <b>Niezatwierdzone</b> miejsca w harmonogramie będą ukryte dla kursantów.
                </div>
              </div>
              <div className="flex flex-col items-start justify-end sm:w-1/2 sm:items-end">
                <ToggleReadinessButton
                  isReady={Boolean(course.data.scheduleReady)}
                  onReady={coursesStatusApi.SCHEDULE_READY}
                  onUnready={coursesStatusApi.SCHEDULE_UNREADY}
                  courseId={course.data.id}
                  readyText="Harmonogram został zatwierdzony"
                  currentStatus="Harmonogram"
                  invalidate={invalidate}
                />
                <ToggleReadinessButton
                  isReady={Boolean(course.data.arePlacesPublic)}
                  onReady={coursesStatusApi.SCHEDULE_PLACES_READY}
                  onUnready={coursesStatusApi.SCHEDULE_PLACES_UNREADY}
                  courseId={course.data.id}
                  readyText="Miejsca zostały zatwierdzone"
                  currentStatus="Harmonogram - miejsca"
                  invalidate={invalidate}
                />
              </div>
            </div>
          </PaziaBox>
          <PaziaBox className="col-span-12">
            <CourseRegistrationSettings
              courseId={course.data.id}
              registrationsClosed={course.data.registrationsClosed}
              allowRegistrationAfterStart={course.data.allowRegistrationAfterStart}
              invalidate={invalidate}
            />
          </PaziaBox>
          <EditCourseSchedulePreview courseId={courseId} />
          <TrainerCostsSection courseId={courseId} className="col-span-12" />
        </div>
      )}
    </TabContent>
  );
};

interface BulkScheduleEditTableProps {
  courseId: number;
  courseGroupId: number;
}
const BulkScheduleEditTable = ({ courseId, courseGroupId }: BulkScheduleEditTableProps) => {
  const { schedule, isLoading, invalidate } = useScheduleEvents(courseId);
  const { loading: loadingSessions, options: sessionsOptions } = useCourseSessionsOptions(courseId);

  const { loading: loadingPlaces, options: placesOptions } = usePlacesOptions();

  if (!schedule) return null;
  return (
    <PaziaBox>
      {sessionsOptions.map((session) => (
        <ScheduleEventTable
          placesOptions={placesOptions}
          events={schedule.events.filter((x) => x.session.id == session.value)}
          session={session}
          courseGroupId={courseGroupId}
          onSaveSingle={(event) => {
            console.log("save single", event);
          }}
          onSaveAll={(events) => {
            console.log("save all", events);
          }}
        />
      ))}
    </PaziaBox>
  );
};

interface ScheduleEventTableProps {
  session: CourseSessionOption;
  events: ScheduleEventApi[];
  placesOptions: IDropdownOption[];
  courseGroupId: number;
  onSaveSingle: (event: ScheduleEventApi) => void;
  onSaveAll: (events: ScheduleEventApi[]) => void;
}
const ScheduleEventTable = ({
  events,
  session,
  placesOptions,
  courseGroupId,
  onSaveSingle,
  onSaveAll,
}: ScheduleEventTableProps) => {
  const medivods = useForm({
    defaultValues: {
      events: events.map((e) => ({
        ...e,
        day: formatDate(e.dateFrom),
        fromTime: fromTimeFormat(e.timeFrom),
        toTime: fromTimeFormat(e.timeTo),
      })),
    },
    mode: "onChange",
  });
  const { control, handleSubmit, formState, getValues } = medivods;
  const { fields } = useFieldArray({
    control,
    keyName: "key",
    name: "events",
  });

  const isDirty = formState.isDirty;

  const saveSingle = (index: number) => {
    const updatedEvent = getValues(`events.${index}`);
    onSaveSingle(updatedEvent);
  };

  const saveAll = () => {
    const updatedEvents = getValues("events");
    onSaveAll(updatedEvents);
  };
  return (
    <FormProvider {...medivods}>
      <form onSubmit={handleSubmit(saveAll)} className="mb-4">
        <div className="text-center text-lg font-semibold">{session.session.name}</div>
        <div>
          <div className="flex gap-1">
            {/* <div className="w-10">Id</div>
            <div>Dzień</div>
            <div>Od</div>
            <div>Do</div>
            <div></div>
            <div>Zakres</div>
            <div>Miejsce</div>
            <div></div>
            <div>Koszta</div> */}
          </div>
          <div>
            {fields.map((field, index) => (
              <>
                <ScheduleEventTableSingleEvent
                  placesOptions={placesOptions}
                  key={field.id}
                  event={field}
                  index={index}
                  session={session}
                  courseGroupId={courseGroupId}
                  onSaveSingle={saveSingle}
                />
              </>
            ))}
          </div>
        </div>
        {/* <button type="submit" disabled={!isDirty}>
          Save All
        </button> */}
      </form>
    </FormProvider>
  );
};

interface ScheduleEventTableSingleEventProps {
  event: ScheduleEventApi;
  index: number;
  session: CourseSessionOption;
  placesOptions: IDropdownOption[];
  courseGroupId: number;
  onSaveSingle: (idx: number) => void;
}
const timeOptions = Array.from({ length: 69 }).map((_, i) => ({
  label: toTimeFormat(300 + i * 15),
  value: 300 + i * 15,
}));
const ScheduleEventTableSingleEvent = ({
  event,
  index,
  session,
  placesOptions,
  courseGroupId,
  onSaveSingle,
}: ScheduleEventTableSingleEventProps) => {
  const [expandedPrices, setExpandedPrices] = useState(false);
  const { watch, setValue, formState } = useFormContext();
  const [fromTimeOptions] = useState(timeOptions);
  const [toTimeOptions, setToTimeOptions] = useState(timeOptions);
  const fromTime = watch(`events.${index}.fromTime`);

  const saveSingle = (index: number) => {
    const updatedEvent = watch(`events.${index}`);
    onSaveSingle(updatedEvent);
  };

  const eventType = event.topicGroupId
    ? "Grupa tematów"
    : event.topicId
      ? "Temat"
      : event.examId
        ? "Egazamin"
        : "Przerwa";

  return (
    <div className="mb-4 flex gap-1 text-sm">
      <DropdownLookALikeBox className="flex min-w-10 items-center justify-center !bg-slate-100">
        {event.id}
      </DropdownLookALikeBox>

      <div>
        <PaziaSelect
          disabled={!session}
          required
          isClearable={false}
          hideIndicator
          name={`events.${index}.day`}
          className="w-28"
          options={getOptionsFromDateToDate(session.session.dateFrom, session.session.dateTo)}
        />
      </div>
      <div>
        <PaziaSelect
          required
          name={`events.${index}.fromTime`}
          hideIndicator
          options={fromTimeOptions}
          className="w-16"
          // onChange={onFromChange}
          isClearable={false}
        />
      </div>
      <div>
        <PaziaSelect
          required
          options={toTimeOptions.filter((x) => x.value > fromTime)}
          // onChange={onToTimeChange}
          className="w-16"
          name={`events.${index}.toTime`}
          hideIndicator
          isClearable={false}
        />
      </div>
      <div className="relative w-72">
        <div className="absolute -top-2 left-2 z-10 bg-white text-xs font-semibold dark:bg-slate-800">{eventType}</div>
        {eventType === "Grupa tematów" && (
          <TopicGroupSelect courseGroupId={courseGroupId} name={`events.${index}.topicGroupId`} hideIndicator />
        )}
        {eventType === "Temat" && (
          <TopicSelect courseGroupId={courseGroupId} name={`events.${index}.topicId`} hideIndicator />
        )}
      </div>
      <div>
        <PaziaSelect
          hideIndicator
          name={`events.${index}.placeType`}
          options={COURSE_PLACE_TYPE_OPTIONS}
          className="w-32"
        />
      </div>
      <div>
        <PaziaSelect hideIndicator options={placesOptions} name={`events.${index}.placeId`} required />
      </div>
      <DropdownLookALikeBox
        className="flex min-w-64 flex-col pl-1 pt-2"
        header={`Łączny koszt: ${toMoneyFormat(event.totalPrice)} PLN`}
        onClick={() => setExpandedPrices(true)}
      >
        {event.trainersPrices.map((x) => (
          <div key={x.trainerId} className="text-xs">
            <span className="text-primary-600">{toMoneyFormat(x.price)} PLN</span>{" "}
            <span>{x.trainerFirstName + " " + x.trainerSurname}</span>
          </div>
        ))}
      </DropdownLookALikeBox>
      <div>
        <button type="button" onClick={() => saveSingle(index)} disabled={!formState.dirtyFields.events?.[index]}>
          Save
        </button>
      </div>
    </div>
  );
};

const DropdownLookALikeBox = ({
  children,
  onClick,
  className = "",
  header,
}: {
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  header?: string;
}) => {
  return (
    <div
      onClick={onClick}
      style={{ borderRadius: "4px" }}
      className={`relative border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-800 ${onClick ? "hover:border-slate-400 dark:hover:border-slate-500" : ""} ${className}`}
    >
      {header && (
        <div className="absolute -top-2 left-2 z-10 bg-white text-xs font-semibold dark:bg-slate-800">{header}</div>
      )}
      {children}
    </div>
  );
};
// duplicate TODO
const getOptionsFromDateToDate = (fromdate: Date, to: Date): IDropdownOption[] => {
  const options: IDropdownOption[] = [];
  const from = new Date(fromdate);

  // set from data time to 2:00
  from.setHours(2, 0, 0, 0);
  // set to data time to 22:00
  to.setHours(22, 0, 0, 0);

  while (from <= to) {
    options.push({ label: formatDate(from), value: formatDate(from) });
    from.setDate(from.getDate() + 1);
  }

  return options;
};

interface TopicGroupSelectProps {
  courseGroupId: number;
  name: string;
  label?: string;
  onChange?: (option: TopicGroupOption) => void;
  hideIndicator?: boolean;
}
export const TopicGroupSelect = ({ courseGroupId, name, label, onChange, hideIndicator }: TopicGroupSelectProps) => {
  const { loading: loadingTopicGroups, options: topicGroupsOptions } = useTopicGroupsOptions(
    `courseGroups in (${courseGroupId})`,
  );
  return (
    <PaziaSelect
      required
      label={label}
      className="col-span-12"
      isLoading={loadingTopicGroups}
      options={topicGroupsOptions}
      components={{ Option }}
      onChange={onChange}
      hideIndicator={hideIndicator}
      name={name}
      tooltip="Wyświetlane są jedynie tematy powiązane z wybraną grupą kursu."
      noOptionsMessage="Brak grup tematów powiązanych z grupą tego kursu — dodaj je w Tematy → Grupy tematów"
    />
  );
};
const Option = (props: any) => {
  const tg = props.data.topicGroup as TopicGroupApi;
  return (
    <div className="text-sm">
      <components.Option {...props} className="!py-1">
        {props.data.label}
        {tg && (
          <div className="flex flex-col">
            <div className="peer -mt-1 w-min text-nowrap text-xs text-gray-400 hover:underline">
              {/* TODO polskie formy  */}
              {minutesToTime(tg.time)} - {tg.topics.length} tematów
            </div>
            <div className="hidden peer-hover:block">
              {tg.topics.map((x) => (
                <span key={x.id} className="text-primary-500 block text-xs">
                  - {x.title}
                </span>
              ))}
            </div>
          </div>
        )}
      </components.Option>
    </div>
  );
};

interface TopicSelectProps {
  courseGroupId: number;
  name: string;
  label?: string;
  onChange?: (option: TopicOption) => void;
  hideIndicator?: boolean;
}
export const TopicSelect = ({ courseGroupId, name, label, hideIndicator, onChange }: TopicSelectProps) => {
  const { loading: loadingTopics, options: topicsOptions } = useTopicsOptions(
    false,
    `courseGroups in (${courseGroupId})`,
  );
  return (
    <PaziaSelect
      required
      className="col-span-12"
      isLoading={loadingTopics}
      options={topicsOptions}
      label={label}
      name={name}
      onChange={onChange}
      hideIndicator={hideIndicator}
      tooltip="Wyświetlane są jedynie tematy powiązane z wybraną grupą kursu."
      noOptionsMessage="Brak tematów powiązanych z grupą tego kursu — ustaw „Grupa kursu” w Tematy"
    />
  );
};
